/**
 * AssignRoleModal Component
 *
 * Modal for assigning and unassigning roles to users, groups, applications, or service accounts.
 * Shows different tabs based on role type:
 * - TENANT roles: Users and Groups tabs
 * - APPLICATION roles: Applications tab only
 * - APP_DOMAIN roles: Users, Groups, and Service Accounts tabs
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import { useRole } from '../hooks/useRole';
import { useAssignRole } from '../hooks/useAssignRole';
import { useUnassignRole } from '../hooks/useUnassignRole';
import { useUsers } from '../../users/hooks/useUsers';
import { useGroups } from '../../groups/hooks/useGroups';
import { useApplications } from '../../applications/hooks/useApplications';
import { useServiceAccounts } from '../../service-accounts/hooks/useServiceAccounts';
import { useAuth } from '../../../contexts/AuthContext';
import { RoleType, type RoleSanitized } from '../../../types/role.types';
import { getErrorMessage } from '../../../lib/utils/errors';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/** System role code for administrator */
const ADMIN_ROLE_CODE = 'ketrics-administrator';

type TabType = 'users' | 'groups' | 'applications' | 'serviceAccounts';

interface AssignRoleModalProps {
  role: RoleSanitized | null;
  tenantId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const AssignRoleModal: React.FC<AssignRoleModalProps> = ({
  role,
  tenantId,
  isOpen,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Get current user to prevent self-removal of admin role
  const { user: currentUser } = useAuth();

  // Fetch fresh role data
  const { data: roleDetails, isLoading: isLoadingRole } = useRole(tenantId, role?.roleId || null);

  // Fetch all users, groups, applications, and service accounts
  const { data: allUsers = [] } = useUsers(tenantId);
  const { data: allGroups = [] } = useGroups(tenantId);
  const { data: allApplications = [] } = useApplications(tenantId);
  const { data: allServiceAccounts = [] } = useServiceAccounts(tenantId);

  const assignRoleMutation = useAssignRole(tenantId);
  const unassignRoleMutation = useUnassignRole(tenantId);

  // Use fresh role data if available, otherwise fall back to prop
  const currentRole = roleDetails || role;

  // Determine available tabs based on role type
  const availableTabs = useMemo((): TabType[] => {
    if (!currentRole) return ['users', 'groups'];
    switch (currentRole.type) {
      case RoleType.APPLICATION:
        return ['applications'];
      case RoleType.APP_DOMAIN:
        return ['users', 'groups', 'serviceAccounts'];
      case RoleType.TENANT:
      default:
        return ['users', 'groups'];
    }
  }, [currentRole?.type]);

  // Auto-select first available tab when role changes or modal opens
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0]);
    }
  }, [availableTabs, activeTab]);

  // Reset search when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Extract IDs from the assignment arrays
  const assignedUserIds = useMemo(() =>
    (currentRole?.users || []).map(u => u.userId),
    [currentRole?.users]
  );
  const assignedGroupIds = useMemo(() =>
    (currentRole?.groups || []).map(g => g.groupId),
    [currentRole?.groups]
  );
  const assignedApplicationIds = useMemo(() =>
    (currentRole?.applications || []).map(a => a.applicationId),
    [currentRole?.applications]
  );
  const assignedServiceAccountIds = useMemo(() =>
    (currentRole?.serviceAccounts || []).map(sa => sa.serviceAccountId),
    [currentRole?.serviceAccounts]
  );

  // Filter available users (not already assigned and active)
  const availableUsers = useMemo(() => {
    return allUsers.filter(
      (user) =>
        !assignedUserIds.includes(user.userId) &&
        user.status === 'active' &&
        (searchQuery === '' ||
          user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
          `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [allUsers, assignedUserIds, searchQuery]);

  // Filter available groups (not already assigned)
  const availableGroups = useMemo(() => {
    return allGroups.filter(
      (group) =>
        !assignedGroupIds.includes(group.groupId) &&
        (searchQuery === '' ||
          group.groupName.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [allGroups, assignedGroupIds, searchQuery]);

  // Filter available applications (not already assigned to this role)
  const availableApplications = useMemo(() => {
    return allApplications.filter(
      (app) =>
        !assignedApplicationIds.includes(app.applicationId) &&
        (searchQuery === '' ||
          app.applicationName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          app.applicationCode.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [allApplications, assignedApplicationIds, searchQuery]);

  // Filter available service accounts (not already assigned and active)
  const availableServiceAccounts = useMemo(() => {
    return allServiceAccounts.filter(
      (sa) =>
        !assignedServiceAccountIds.includes(sa.serviceAccountId) &&
        sa.status === 'active' &&
        (searchQuery === '' ||
          sa.serviceAccountName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          sa.serviceAccountCode.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [allServiceAccounts, assignedServiceAccountIds, searchQuery]);

  if (!role) return null;

  // Get assigned entities
  const assignedUsers = allUsers.filter((user) => assignedUserIds.includes(user.userId));
  const assignedGroups = allGroups.filter((group) => assignedGroupIds.includes(group.groupId));
  const assignedApplications = allApplications.filter((app) => assignedApplicationIds.includes(app.applicationId));
  const assignedServiceAccounts = allServiceAccounts.filter((sa) => assignedServiceAccountIds.includes(sa.serviceAccountId));

  // Assign user
  const handleAssignUser = async (userId: string) => {
    if (!currentRole) return;
    try {
      await assignRoleMutation.mutateAsync({
        roleId: currentRole.roleId,
        data: { users: [userId] },
      });
    } catch (error) {
      console.error('Failed to assign user:', error);
    }
  };

  // Unassign user
  const handleUnassignUser = async (userId: string) => {
    if (!currentRole) return;
    try {
      await unassignRoleMutation.mutateAsync({
        roleId: currentRole.roleId,
        data: { users: [userId] },
      });
    } catch (error) {
      console.error('Failed to unassign user:', error);
    }
  };

  // Assign group
  const handleAssignGroup = async (groupId: string) => {
    if (!currentRole) return;
    try {
      await assignRoleMutation.mutateAsync({
        roleId: currentRole.roleId,
        data: { groups: [groupId] },
      });
    } catch (error) {
      console.error('Failed to assign group:', error);
    }
  };

  // Unassign group
  const handleUnassignGroup = async (groupId: string) => {
    if (!currentRole) return;
    try {
      await unassignRoleMutation.mutateAsync({
        roleId: currentRole.roleId,
        data: { groups: [groupId] },
      });
    } catch (error) {
      console.error('Failed to unassign group:', error);
    }
  };

  // Assign application
  const handleAssignApplication = async (applicationId: string) => {
    if (!currentRole) return;
    try {
      await assignRoleMutation.mutateAsync({
        roleId: currentRole.roleId,
        data: { applicationIds: [applicationId] },
      });
    } catch (error) {
      console.error('Failed to assign application:', error);
    }
  };

  // Unassign application
  const handleUnassignApplication = async (applicationId: string) => {
    if (!currentRole) return;
    try {
      await unassignRoleMutation.mutateAsync({
        roleId: currentRole.roleId,
        data: { applicationIds: [applicationId] },
      });
    } catch (error) {
      console.error('Failed to unassign application:', error);
    }
  };

  // Assign service account
  const handleAssignServiceAccount = async (serviceAccountId: string) => {
    if (!currentRole) return;
    try {
      await assignRoleMutation.mutateAsync({
        roleId: currentRole.roleId,
        data: { serviceAccountIds: [serviceAccountId] },
      });
    } catch (error) {
      console.error('Failed to assign service account:', error);
    }
  };

  // Unassign service account
  const handleUnassignServiceAccount = async (serviceAccountId: string) => {
    if (!currentRole) return;
    try {
      await unassignRoleMutation.mutateAsync({
        roleId: currentRole.roleId,
        data: { serviceAccountIds: [serviceAccountId] },
      });
    } catch (error) {
      console.error('Failed to unassign service account:', error);
    }
  };

  const isLoading = assignRoleMutation.isPending || unassignRoleMutation.isPending;
  const error = assignRoleMutation.error || unassignRoleMutation.error;

  // Check if user can be unassigned from this role
  // Admins cannot remove themselves from ketrics-administrator role
  const canUnassignUser = (userId: string): boolean => {
    if (currentRole?.roleCode === ADMIN_ROLE_CODE && userId === currentUser?.userId) {
      return false;
    }
    return true;
  };

  // Check if an application already has a different role assigned
  const getExistingRoleForApp = (app: { role?: { roleId: string; roleName: string } }): string | null => {
    if (app.role && app.role.roleId !== currentRole?.roleId) {
      return app.role.roleName;
    }
    return null;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>Manage Role Assignments: {currentRole?.roleName || 'Loading...'}</Modal.Header>

      <Modal.Body>
        {isLoadingRole ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Error Alert */}
            {error && (
              <Alert variant="error">
                {getErrorMessage(error)}
              </Alert>
            )}

            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {availableTabs.includes('users') && (
                  <button
                    onClick={() => setActiveTab('users')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'users'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Users ({assignedUserIds.length})
                  </button>
                )}
                {availableTabs.includes('groups') && (
                  <button
                    onClick={() => setActiveTab('groups')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'groups'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Groups ({assignedGroupIds.length})
                  </button>
                )}
                {availableTabs.includes('applications') && (
                  <button
                    onClick={() => setActiveTab('applications')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'applications'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Applications ({assignedApplicationIds.length})
                  </button>
                )}
                {availableTabs.includes('serviceAccounts') && (
                  <button
                    onClick={() => setActiveTab('serviceAccounts')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'serviceAccounts'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Service Accounts ({assignedServiceAccountIds.length})
                  </button>
                )}
              </nav>
            </div>

            {/* Search */}
            <div>
              <Input
                type="text"
                placeholder={`Search ${activeTab}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-4">
                {/* Currently Assigned Users */}
                {assignedUsers.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Assigned Users ({assignedUsers.length})
                    </h4>
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-2 max-h-40 overflow-y-auto">
                      {assignedUsers.map((user) => (
                        <div
                          key={user.userId}
                          className="flex items-center justify-between bg-white p-2 rounded border"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {user.firstName} {user.lastName}
                            </p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleUnassignUser(user.userId)}
                            loading={isLoading}
                            disabled={!canUnassignUser(user.userId)}
                            title={!canUnassignUser(user.userId) ? 'Cannot remove admin role from yourself' : 'Remove from role'}
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Available Users */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Available Users ({availableUsers.length})
                  </h4>
                  {availableUsers.length > 0 ? (
                    <div className="border border-gray-200 rounded-md p-3 space-y-2 max-h-60 overflow-y-auto">
                      {availableUsers.map((user) => (
                        <div
                          key={user.userId}
                          className="flex items-center justify-between bg-gray-50 p-2 rounded hover:bg-gray-100"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {user.firstName} {user.lastName}
                            </p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleAssignUser(user.userId)}
                            loading={isLoading}
                          >
                            Assign
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">
                      {searchQuery ? 'No users found matching your search' : 'All users have been assigned this role'}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Groups Tab */}
            {activeTab === 'groups' && (
              <div className="space-y-4">
                {/* Currently Assigned Groups */}
                {assignedGroups.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Assigned Groups ({assignedGroups.length})
                    </h4>
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-2 max-h-40 overflow-y-auto">
                      {assignedGroups.map((group) => (
                        <div
                          key={group.groupId}
                          className="flex items-center justify-between bg-white p-2 rounded border"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{group.groupName}</p>
                            <p className="text-xs text-gray-500">
                              {(group.users?.length || 0)} user{(group.users?.length || 0) !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleUnassignGroup(group.groupId)}
                            loading={isLoading}
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Available Groups */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Available Groups ({availableGroups.length})
                  </h4>
                  {availableGroups.length > 0 ? (
                    <div className="border border-gray-200 rounded-md p-3 space-y-2 max-h-60 overflow-y-auto">
                      {availableGroups.map((group) => (
                        <div
                          key={group.groupId}
                          className="flex items-center justify-between bg-gray-50 p-2 rounded hover:bg-gray-100"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{group.groupName}</p>
                            <p className="text-xs text-gray-500">
                              {(group.users?.length || 0)} user{(group.users?.length || 0) !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleAssignGroup(group.groupId)}
                            loading={isLoading}
                          >
                            Assign
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">
                      {searchQuery ? 'No groups found matching your search' : 'All groups have been assigned this role'}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Applications Tab */}
            {activeTab === 'applications' && (
              <div className="space-y-4">
                {/* Currently Assigned Applications */}
                {assignedApplications.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Assigned Applications ({assignedApplications.length})
                    </h4>
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-2 max-h-40 overflow-y-auto">
                      {assignedApplications.map((app) => (
                        <div
                          key={app.applicationId}
                          className="flex items-center justify-between bg-white p-2 rounded border"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{app.applicationName}</p>
                            <p className="text-xs text-gray-500">{app.applicationCode}</p>
                          </div>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleUnassignApplication(app.applicationId)}
                            loading={isLoading}
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Available Applications */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Available Applications ({availableApplications.length})
                  </h4>
                  {availableApplications.length > 0 ? (
                    <div className="border border-gray-200 rounded-md p-3 space-y-2 max-h-60 overflow-y-auto">
                      {availableApplications.map((app) => {
                        const existingRole = getExistingRoleForApp(app);
                        return (
                          <div
                            key={app.applicationId}
                            className="flex items-center justify-between bg-gray-50 p-2 rounded hover:bg-gray-100"
                          >
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">{app.applicationName}</p>
                              <p className="text-xs text-gray-500">{app.applicationCode}</p>
                              {existingRole && (
                                <div className="flex items-center gap-1 mt-1">
                                  <ExclamationTriangleIcon className="h-3 w-3 text-amber-500" />
                                  <p className="text-xs text-amber-600">
                                    Currently assigned to: {existingRole}
                                  </p>
                                </div>
                              )}
                            </div>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleAssignApplication(app.applicationId)}
                              loading={isLoading}
                            >
                              {existingRole ? 'Reassign' : 'Assign'}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">
                      {searchQuery ? 'No applications found matching your search' : 'All applications have been assigned this role'}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Service Accounts Tab */}
            {activeTab === 'serviceAccounts' && (
              <div className="space-y-4">
                {/* Currently Assigned Service Accounts */}
                {assignedServiceAccounts.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Assigned Service Accounts ({assignedServiceAccounts.length})
                    </h4>
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-2 max-h-40 overflow-y-auto">
                      {assignedServiceAccounts.map((sa) => (
                        <div
                          key={sa.serviceAccountId}
                          className="flex items-center justify-between bg-white p-2 rounded border"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{sa.serviceAccountName}</p>
                            <p className="text-xs text-gray-500">{sa.serviceAccountCode}</p>
                          </div>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleUnassignServiceAccount(sa.serviceAccountId)}
                            loading={isLoading}
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Available Service Accounts */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Available Service Accounts ({availableServiceAccounts.length})
                  </h4>
                  {availableServiceAccounts.length > 0 ? (
                    <div className="border border-gray-200 rounded-md p-3 space-y-2 max-h-60 overflow-y-auto">
                      {availableServiceAccounts.map((sa) => (
                        <div
                          key={sa.serviceAccountId}
                          className="flex items-center justify-between bg-gray-50 p-2 rounded hover:bg-gray-100"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{sa.serviceAccountName}</p>
                            <p className="text-xs text-gray-500">{sa.serviceAccountCode}</p>
                          </div>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleAssignServiceAccount(sa.serviceAccountId)}
                            loading={isLoading}
                          >
                            Assign
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">
                      {searchQuery ? 'No service accounts found matching your search' : 'All service accounts have been assigned this role'}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={onClose}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
